import { IntegrationProvider } from "@repo/app-types/entities/integration";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { Size } from "@repo/ui/helpers/helpers";
import { KnowledgeBaseSyncProviderLogo } from "@repo/ui/knowledgeBaseSync/knowledgeBaseSyncProviderLogo";
import { type FC, type ReactNode, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router";
import {
  useDisconnectGenesysIntegrationMutation,
  useGenesysIntegration,
} from "../../../api/entities/genesysIntegration.js";
import {
  useDeleteKnowledgeBaseSyncIntegrationMutation,
  useKnowledgeBaseSyncIntegrationStatus,
} from "../../../api/entities/knowledgeBaseSyncIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { appPaths } from "../../../app/appPaths.js";
import genesysLogoSrc from "../../../assets/integrations/genesysLogo.svg";
import slackLogoSrc from "../../../assets/integrations/slackLogo.png";
import teamsLogoSrc from "../../../assets/integrations/teamsLogo.png";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { openOAuthSigninPopupForIntegration } from "../../../helpers/urlHelpers.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  deleteIntegration,
  selectAllIntegrations,
} from "../../integrations/integrationsSlice.js";
import { settingsSections } from "../settingsSections.js";
import { useSettingsSectionsPolicy } from "../settingsSectionsPolicy.js";
import { SettingsSectionPage } from "../shared/settingsSection.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { GenesysIntegrationSettings } from "./genesysIntegrationSettings.js";
import { GuruIntegrationSettings } from "./guruIntegrationSettings.js";
import { KustomerIntegrationSettings } from "./kustomerIntegrationSettings.js";
import {
  TeamsConnectionStatus,
  TeamsIntegrationTableStatus,
  useTeamsInstalledTeams,
} from "./teamsConnectionStatus.js";

// Registry of the integrations rendered in the overview table and detail pages.
// Add an entry to surface a new integration.
type IntegrationConfig = {
  provider: IntegrationProvider;
  slug: string;
  name: string;
  description: string;
  disconnectWarning: string;
};

const integrationConfigs: IntegrationConfig[] = [
  {
    provider: IntegrationProvider.SLACK,
    slug: "slack",
    name: "Slack",
    description:
      "Connect your Slack account so agents can send results to the right place. Agent results can be delivered to specific Slack channels or sent as DMs to specific teammates.",
    disconnectWarning:
      "Disconnecting Slack will stop agent results from being delivered to Slack.",
  },
  {
    provider: IntegrationProvider.TEAMS,
    slug: "microsoft-teams",
    name: "Microsoft Teams",
    description:
      "Connect your Microsoft 365 tenant so agents can send results to the right place. Agent results can be delivered to specific Teams channels or sent as DMs to specific teammates.",
    disconnectWarning:
      "Disconnecting Microsoft Teams will stop agent results from being delivered to Teams.",
  },
  {
    provider: IntegrationProvider.GURU,
    slug: "guru",
    name: "Guru",
    description:
      "Sync Guru collections into Alpharun knowledge bases so agents answer from the same articles your team does. Each collection becomes its own read-only knowledge base, kept up to date in the background.",
    disconnectWarning:
      "Disconnecting Guru will stop its knowledge bases from being kept up to date.",
  },
  {
    provider: IntegrationProvider.KUSTOMER,
    slug: "kustomer",
    name: "Kustomer",
    description:
      "Connect Kustomer so its knowledge bases are synced into read-only Alpharun knowledge bases.",
    disconnectWarning:
      "Disconnecting Kustomer will stop its knowledge bases from being kept up to date.",
  },
  {
    provider: IntegrationProvider.GENESYS,
    slug: "genesys",
    name: "Genesys Cloud",
    description:
      "Import Genesys Cloud call recordings into Alpharun for transcription, coaching, and performance analysis.",
    disconnectWarning:
      "Disconnecting Genesys Cloud will stop new call recordings from syncing.",
  },
];

const renderIntegrationLogo = (provider: IntegrationProvider) => {
  let logo: ReactNode;
  switch (provider) {
    case IntegrationProvider.SLACK:
      logo = (
        <img
          src={slackLogoSrc}
          alt=""
          aria-hidden="true"
          className="size-4 object-contain"
        />
      );
      break;
    case IntegrationProvider.TEAMS:
      logo = (
        <img
          src={teamsLogoSrc}
          alt=""
          aria-hidden="true"
          className="size-4 object-contain"
        />
      );
      break;
    case IntegrationProvider.GURU:
    case IntegrationProvider.KUSTOMER:
      logo = (
        <KnowledgeBaseSyncProviderLogo provider={provider} className="size-4" />
      );
      break;
    case IntegrationProvider.GENESYS:
      logo = (
        <img
          src={genesysLogoSrc}
          alt=""
          aria-hidden="true"
          className="size-4 object-contain"
        />
      );
      break;
    default:
      return null;
  }

  return (
    <span className="flex size-6 shrink-0 items-center justify-center rounded-sm border border-border-light bg-card">
      {logo}
    </span>
  );
};

export const IntegrationSettingsSection: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { integrationSlug } = useParams<{ integrationSlug?: string }>();
  const { sections } = useSettingsSectionsPolicy();
  const allIntegrations = useAppSelector(selectAllIntegrations);
  const isTeamsEnabled = useIsFeatureFlagEnabled(FeatureFlagKey.MicrosoftTeams);
  const isGenesysEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.GenesysIntegration,
  );
  const [disconnectTarget, setDisconnectTarget] =
    useState<IntegrationConfig | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const integrationsByProvider = new Map(
    allIntegrations.map((integration) => [integration.provider, integration]),
  );

  // Guru's state comes from its own route rather than the boot payload: the
  // stored settings carry an encrypted token per collection, which no customer
  // response may include, so the API returns a non-secret view instead.
  // Fetched whenever this surface is available, then shared by the overview
  // row and the detail form.
  const isGuruDetail =
    integrationSlug === "guru" && sections.knowledgeBases.canAccess;
  const isKustomerDetail =
    integrationSlug === "kustomer" && sections.knowledgeBases.canAccess;
  const isKnowledgeBaseSyncDetail = isGuruDetail || isKustomerDetail;
  const guruStatus = useKnowledgeBaseSyncIntegrationStatus(
    "guru",
    sections.knowledgeBases.canAccess,
  );
  const kustomerStatus = useKnowledgeBaseSyncIntegrationStatus(
    "kustomer",
    sections.knowledgeBases.canAccess,
  );
  const [deleteKnowledgeBaseSyncIntegration] =
    useDeleteKnowledgeBaseSyncIntegrationMutation();
  const guruIntegration =
    guruStatus.currentData?.integration?.provider === "guru"
      ? guruStatus.currentData.integration
      : null;
  const kustomerIntegration =
    kustomerStatus.currentData?.integration?.provider === "kustomer"
      ? kustomerStatus.currentData.integration
      : null;

  // Genesys reads from its own route for the same reason Guru does — its
  // settings carry the encrypted client secret. Fetched whenever the flag is on,
  // rather than only on the detail page, so the overview row can say whether the
  // sync is live or paused.
  const genesysStatus = useGenesysIntegration(isGenesysEnabled);
  const genesysIntegration = genesysStatus.currentData?.integration ?? null;
  const [disconnectGenesysIntegration] =
    useDisconnectGenesysIntegrationMutation();

  const getIntegrationId = (provider: IntegrationProvider): string | null => {
    if (provider === IntegrationProvider.GURU) {
      return guruIntegration?.integration_id ?? null;
    }
    if (provider === IntegrationProvider.KUSTOMER) {
      return kustomerIntegration?.integration_id ?? null;
    }
    if (provider === IntegrationProvider.GENESYS) {
      return genesysIntegration?.id ?? null;
    }
    return integrationsByProvider.get(provider)?.id ?? null;
  };

  // Fetched once here and shared by the overview table's Teams status cell
  // and the Teams detail card, so navigating between them reuses the result.
  const teamsIntegration =
    integrationsByProvider.get(IntegrationProvider.TEAMS) ?? null;
  const { teamsWithInstalledAppFetch, retryFetch } = useTeamsInstalledTeams(
    isTeamsEnabled ? (teamsIntegration?.id ?? null) : null,
  );

  const visibleIntegrationConfigs = integrationConfigs.filter(
    (config) =>
      (config.provider !== IntegrationProvider.TEAMS || isTeamsEnabled) &&
      (config.provider !== IntegrationProvider.GENESYS || isGenesysEnabled) &&
      ((config.provider !== IntegrationProvider.GURU &&
        config.provider !== IntegrationProvider.KUSTOMER) ||
        sections.knowledgeBases.canAccess),
  );

  const selectedIntegration = integrationConfigs.find(
    (config) =>
      config.slug === integrationSlug &&
      ((config.provider !== IntegrationProvider.GURU &&
        config.provider !== IntegrationProvider.KUSTOMER) ||
        isKnowledgeBaseSyncDetail) &&
      (config.provider !== IntegrationProvider.GENESYS || isGenesysEnabled),
  );

  const renderConnectedStatus = () => (
    <span className="text-xs font-medium text-success">Connected</span>
  );

  /** Disconnects through the provider's canonical customer route. */
  const handleDisconnect = async (config: IntegrationConfig) => {
    setDisconnectError(null);

    const integrationId = getIntegrationId(config.provider);
    if (!integrationId) return;

    try {
      switch (config.provider) {
        case IntegrationProvider.GENESYS:
          await disconnectGenesysIntegration({ integrationId }).unwrap();
          break;
        case IntegrationProvider.GURU:
        case IntegrationProvider.KUSTOMER:
          await deleteKnowledgeBaseSyncIntegration({
            provider: config.provider,
            integrationId,
          }).unwrap();
          break;
        default:
          // Still boot-backed, so it disconnects through the Redux thunk, which
          // keeps its own error state rather than surfacing one here.
          dispatch(deleteIntegration(integrationId));
      }
    } catch (error) {
      setDisconnectError(
        getApiErrorMessage(
          error,
          `Could not disconnect ${config.name} — please try again.`,
        ),
      );
    }
  };

  const renderIntegrationAction = (config: IntegrationConfig) => {
    if (getIntegrationId(config.provider)) {
      return (
        <Can
          I={PermissionActions.DELETE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <Button
              size={Size.SMALL}
              variant={ButtonVariant.Warning}
              isDisabled={!allowed}
              onClick={() => setDisconnectTarget(config)}
            >
              Disconnect
            </Button>
          )}
        </Can>
      );
    }

    if (
      config.provider !== IntegrationProvider.SLACK &&
      config.provider !== IntegrationProvider.TEAMS
    ) {
      return null;
    }

    return (
      <Can
        I={PermissionActions.CREATE}
        a={PermissionSubjects.INTEGRATIONS}
        passThrough={true}
      >
        {(allowed) => (
          <Button
            size={Size.SMALL}
            variant={ButtonVariant.Primary}
            isDisabled={!allowed}
            onClick={() => openOAuthSigninPopupForIntegration(config.provider)}
          >
            Connect
          </Button>
        )}
      </Can>
    );
  };

  const renderIntegrationTableStatus = (config: IntegrationConfig) => {
    // Genesys connects by entering credentials on its detail page rather than
    // through an OAuth popup, so its overview button navigates there.
    if (config.provider === IntegrationProvider.GENESYS) {
      if (!genesysIntegration) {
        return (
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.INTEGRATIONS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                size={Size.SMALL}
                variant={ButtonVariant.Primary}
                isDisabled={!allowed}
                onClick={() =>
                  navigate(
                    appPaths.settingsIntegration.build({
                      integrationSlug: config.slug,
                    }),
                  )
                }
              >
                Connect
              </Button>
            )}
          </Can>
        );
      }
      // Reads "Connected" like every other row when it is working normally, and
      // only diverges for the state the others cannot be in — a connection whose
      // sync the customer has paused, which the overview should not hide.
      return genesysIntegration.settings.is_enabled ? (
        renderConnectedStatus()
      ) : (
        <span className="text-xs font-medium text-amber-500">Paused</span>
      );
    }

    if (!getIntegrationId(config.provider)) {
      return renderIntegrationAction(config);
    }
    if (config.provider === IntegrationProvider.TEAMS) {
      return (
        <TeamsIntegrationTableStatus
          installedTeamsFetch={teamsWithInstalledAppFetch}
        />
      );
    }
    return renderConnectedStatus();
  };

  // Connected integrations first, then alphabetically by name within each group.
  const sortedIntegrations = [...visibleIntegrationConfigs].sort(
    (firstIntegration, secondIntegration) =>
      Number(Boolean(getIntegrationId(secondIntegration.provider))) -
        Number(Boolean(getIntegrationId(firstIntegration.provider))) ||
      firstIntegration.name.localeCompare(secondIntegration.name),
  );

  const renderOverview = () => (
    <div className="space-y-6">
      <SettingsTable>
        <SettingsTableHeaderSection>
          <tr>
            <SettingsTableHeaderCell className="w-full">
              Integration
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-32">
              Status
            </SettingsTableHeaderCell>
          </tr>
        </SettingsTableHeaderSection>
        <SettingsTableBodySection>
          {sortedIntegrations.map((integration) => (
            <tr
              key={integration.slug}
              className="cursor-pointer hover:bg-muted"
              role="link"
              tabIndex={0}
              onClick={() =>
                navigate(
                  appPaths.settingsIntegration.build({
                    integrationSlug: integration.slug,
                  }),
                )
              }
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  navigate(
                    appPaths.settingsIntegration.build({
                      integrationSlug: integration.slug,
                    }),
                  );
                }
              }}
            >
              <SettingsTableDataCell>
                <div className="flex items-center gap-2.5">
                  {renderIntegrationLogo(integration.provider)}
                  <div className="font-semibold">{integration.name}</div>
                </div>
              </SettingsTableDataCell>
              <SettingsTableDataCell className="whitespace-nowrap">
                <div
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {renderIntegrationTableStatus(integration)}
                </div>
              </SettingsTableDataCell>
            </tr>
          ))}
        </SettingsTableBodySection>
      </SettingsTable>
    </div>
  );

  const renderDetail = (integration: IntegrationConfig) => {
    const integrationId = getIntegrationId(integration.provider);

    return (
      <div className="space-y-6">
        <RouterLink
          to={appPaths.settingsIntegrations.build()}
          className="inline-flex text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          Back to integrations
        </RouterLink>

        <div className="flex items-start gap-3">
          {renderIntegrationLogo(integration.provider)}
          <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
            {integration.description}
          </p>
        </div>

        {/* Teams connects in two parts (consent + org-wide app install), so its
            detail page breaks the status down instead of a bare "Connected". */}
        {integration.provider === IntegrationProvider.TEAMS && integrationId ? (
          <TeamsConnectionStatus
            integrationId={integrationId}
            installedTeamsFetch={teamsWithInstalledAppFetch}
            onRetryInstalledTeams={retryFetch}
          />
        ) : null}

        {/* Guru is configured, not just connected: the collections to sync and
            their tokens are entered here, both to set it up and to change it. */}
        {integration.provider === IntegrationProvider.GURU ? (
          <GuruIntegrationSettings
            integration={guruIntegration}
            activeSyncRun={guruStatus.currentData?.active_sync_run ?? null}
            isLoading={!guruStatus.currentData && !guruStatus.isError}
            isError={guruStatus.isError && !guruStatus.currentData}
            onReload={() => void guruStatus.refetch()}
          />
        ) : null}
        {integration.provider === IntegrationProvider.KUSTOMER ? (
          <KustomerIntegrationSettings
            integration={kustomerIntegration}
            activeSyncRun={kustomerStatus.currentData?.active_sync_run ?? null}
            isLoading={!kustomerStatus.currentData && !kustomerStatus.isError}
            isError={kustomerStatus.isError && !kustomerStatus.currentData}
            onReload={() => void kustomerStatus.refetch()}
          />
        ) : null}

        {/* Genesys is credentials, not OAuth: the client id, secret and region
            are entered here and tested against the org before anything saves. */}
        {integration.provider === IntegrationProvider.GENESYS ? (
          <GenesysIntegrationSettings
            integration={genesysIntegration}
            isLoading={!genesysStatus.currentData && !genesysStatus.isError}
            isError={genesysStatus.isError && !genesysStatus.currentData}
            onReload={() => void genesysStatus.refetch()}
            onRequestDisconnect={() => setDisconnectTarget(integration)}
          />
        ) : null}
      </div>
    );
  };

  return (
    <SettingsSectionPage
      title={
        selectedIntegration
          ? selectedIntegration.name
          : settingsSections.integrations.label
      }
      actions={
        // Genesys carries its own Disconnect section at the foot of its detail
        // page, so it opts out of the header button rather than showing two.
        selectedIntegration &&
        selectedIntegration.provider !== IntegrationProvider.GENESYS &&
        getIntegrationId(selectedIntegration.provider)
          ? renderIntegrationAction(selectedIntegration)
          : undefined
      }
    >
      {/* A refused disconnect is the customer's to act on (a sync is running,
          or its knowledge bases still need resolving), so it is said out here
          rather than swallowed with the modal. */}
      {disconnectError ? (
        <p className="mb-4 text-sm text-red-600">{disconnectError}</p>
      ) : null}

      {selectedIntegration
        ? renderDetail(selectedIntegration)
        : renderOverview()}

      <SimpleModal
        isOpen={disconnectTarget !== null}
        onCancel={() => setDisconnectTarget(null)}
        variant={SimpleModalVariant.Warning}
        title={disconnectTarget ? `Disconnect ${disconnectTarget.name}?` : ""}
        subtitle={disconnectTarget?.disconnectWarning}
        confirmButtonText="Disconnect"
        onConfirm={() => {
          if (disconnectTarget) {
            void handleDisconnect(disconnectTarget);
          }

          setDisconnectTarget(null);
        }}
      />
    </SettingsSectionPage>
  );
};
